/**
 * DiseaseAutocomplete — single-line text input backed by the 5,000-disease
 * catalog. Shows up to `limit` matches as the user types.
 *
 * Self-contained: no shared hook (the hook indirection caused stale-closure
 * bugs where the second fetch never fired). State is kept local to the
 * component and we trigger the search effect explicitly on each query change.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { prefetchSynthesis } from "../lib/prefetch";

export default function DiseaseAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Search any condition · live AI synthesis…",
  className = "",
  testid = "disease-ac",
  limit = 12,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [related, setRelated] = useState({});
  const [tier, setTier] = useState("");
  const [loading, setLoading] = useState(false);
  const wrap = useRef(null);

  // Single source of truth for the typed text — `value` from the parent.
  // We don't mirror it into local state because the duplication caused
  // a race where setQuery from the parent prop sync clobbered the typed
  // query before the debounce fired.
  const query = value || "";

  useEffect(() => {
    function onDoc(e) {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      // Use the new /suggest endpoint which gives us typo-tolerance
      // ("diabetis" → "diabetes mellitus") plus a related-concepts
      // web off the top hit. Falls back gracefully if backend doesn't
      // serve /suggest (older instances).
      const params = { limit };
      if (term) params.q = term;
      api.get("/catalog/diseases/suggest", { params })
        .then((r) => {
          if (cancelled) return;
          setItems(r.data?.items || []);
          setCorrections(r.data?.corrections || []);
          setRelated(r.data?.related || {});
          setTier(r.data?.tier || "");
        })
        .catch(() => {
          if (cancelled) return;
          // Legacy fallback
          api.get("/catalog/diseases/search", { params })
            .then((r) => {
              if (!cancelled) setItems(r.data?.items || []);
            })
            .catch(() => { if (!cancelled) setItems([]); });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, term ? 180 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open, limit]);

  function pick(it) {
    onChange?.(it.name);
    onSelect?.(it);
    setOpen(false);
  }

  // Build a synthetic item from the user's typed text so they can
  // submit a disease the curated catalog doesn't index. Slug shape
  // matches the rest of the app (lowercase, non-alphanumeric → _).
  function pickFreeText() {
    const term = (query || "").trim();
    if (!term) return;
    const slug = term.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    pick({ name: term, doid: slug, system: null });
  }

  return (
    <div ref={wrap} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            // Prefer the top dropdown hit if there is one; otherwise
            // accept the user's free text as a custom condition.
            if (items.length > 0) pick(items[0]);
            else pickFreeText();
          }
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full bg-background border border-white/8 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6B8F71]/60 ${className}`}
        autoComplete="off"
      />
      {open && (query.trim() || items.length > 0) && (
        <div
          data-testid={`${testid}-list`}
          className="absolute z-30 mt-1 w-full max-h-96 overflow-auto rounded-lg border border-white/10 bg-[transparent] shadow-2xl backdrop-blur"
        >
          {loading && (
            <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-white/40">
              Searching…
            </div>
          )}
          {/* Typo correction row (fuzzy hits only) */}
          {!loading && tier === "fuzzy" && corrections.length > 0 && (
            <div
              data-testid={`${testid}-corrections`}
              className="px-3 py-2 border-b border-white/10"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#6B8F71] mb-1.5">
                Did you mean?
              </p>
              <div className="flex flex-wrap gap-1.5">
                {corrections.slice(0, 3).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onChange?.(c)}
                    className="px-2 py-0.5 rounded-full text-[11px] bg-[#6B8F71]/15 text-[#6B8F71] border border-[#6B8F71]/35 hover:bg-[#6B8F71]/25"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!loading && items.length === 0 && query.trim() && (
            // CLICKABLE fallback — when the curated catalog returns no
            // hits, give the user a one-tap path to the live AI synthesis
            // page for whatever they typed. Without this, off-catalog
            // queries (long covid, post-viral fatigue, etc.) had no
            // way through.
            <button
              type="button"
              onClick={pickFreeText}
              data-testid={`${testid}-freetext`}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-[#6B8F71]/15"
            >
              <span className="text-white break-words">
                Open <em className="not-italic text-[#6B8F71]">"{query.trim()}"</em>
              </span>
              <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-[#6B8F71]">
                live AI →
              </span>
            </button>
          )}
          {items.map((it, idx) => (
            <div key={it.doid || it.name}>
              <button
                type="button"
                onClick={() => pick(it)}
                onMouseEnter={() => prefetchSynthesis({ kind: "condition", name: it.name })}
                onFocus={() => prefetchSynthesis({ kind: "condition", name: it.name })}
                data-testid={`${testid}-row-${it.doid || it.name}`}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-[#6B8F71]/15"
              >
                <span className="text-white break-words">{it.name}</span>
                {it.system && (
                  <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-[#6B8F71]">
                    {it.system}
                  </span>
                )}
              </button>
              {/* Related-web — shown directly under the top hit for context */}
              {idx === 0 && related[it.doid]?.length > 0 && (
                <div
                  data-testid={`${testid}-related`}
                  className="px-3 py-2 border-b border-white/8"
                  style={{ background: "rgba(143,190,222,0.04)" }}
                >
                  <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#6B8F71] mb-1.5">
                    Related to {it.name}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {related[it.doid].slice(0, 4).map((s) => (
                      <button
                        key={s.doid}
                        type="button"
                        onClick={() => pick(s)}
                        onMouseEnter={() => prefetchSynthesis({ kind: "condition", name: s.name })}
                        data-testid={`${testid}-related-${s.doid}`}
                        className="px-2 py-0.5 rounded-full text-[11px] border border-white/15 text-white/75 hover:border-[#6B8F71]/50 hover:bg-white/10/10"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {/* Always offer a "go with my typed text" footer when results
              exist too — handy when the curated hit isn't quite what
              the user meant. */}
          {!loading && items.length > 0 && query.trim() && (
            <button
              type="button"
              onClick={pickFreeText}
              data-testid={`${testid}-freetext-footer`}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs border-t border-white/8 hover:bg-[#6B8F71]/10 text-white/70"
            >
              <span className="break-words">
                Or just open <em className="not-italic text-[#6B8F71]">"{query.trim()}"</em> as typed
              </span>
              <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-[#6B8F71]">
                live AI →
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
